import { PayloadAction } from '@reduxjs/toolkit';
import {
  AccountAdminDto,
  ProjectAdminDto,
  ResourceDto,
  ResourceStatus,
  UserForAdminDto,
} from '../../types';
import { SowAdminState } from './sow-admin.types';
import { createSliceCustom } from '../../utils/tests/createSliceCustom';
import { DataState } from '../../types/asyncData';
import { sowAdminStoreKey } from './sow-admin.const';
import {
  executeSowChangeReq,
  fetchSowAdminAccounts,
  fetchSowChangeReqs,
  rejectSowChangeReq,
  requestEdit,
  requestMove,
  requestDeactivate,
  requestBulkDeactivate,
} from './sow-admin.thunks';
import { extOperStatusToBoolean } from '../../utils/business';
import {
  ExecuteSowChangeReqRespDto,
  SowChangeReqDto,
  SowChangeReqForApprovalRespDto,
} from '../../types/sow-change-req';

export const initialState: SowAdminState = {
  accounts: null,
  projects: null,
  state: DataState.Idle,
  error: null,

  dateLocal: null,
  date: null,

  portfolioAccounts: null,
  portfolioProjects: null,
  portfolioState: DataState.Idle,
  portfolioError: null,
  portfolioDateLocal: null,
  portfolioDate: null,

  sowChangeReqsState: DataState.Idle,
  sowChangeReqsError: null,
  sowChangeReqId: null,
  sowChangeReqs: null,
  sowChangeReqStatuses: {},

  executeSowChangeReqState: DataState.Idle,
  executeSowChangeReqError: null,

  rejectSowChangeReqState: DataState.Idle,
  rejectSowChangeReqError: null,

  requestMoveState: DataState.Idle,
  requestMoveError: null,
  requestEditState: DataState.Idle,
  requestEditError: null,
  requestDeactivateState: DataState.Idle,
  requestDeactivateError: null,

  isUniqueFilterListsDefined: false,
  uniqueFilterLists: {
    pdms: [],
    dms: [],
  },
  filters: {
    PDMsToFilter: [],
    DMsToFilter: [],
  },
};

const recomputePendingCounts = (
  account: AccountAdminDto,
  project: ProjectAdminDto,
): void => {
  project.pendingRequestsCount = (project.members ?? []).filter(
    (m) => m.isDeleteReqPending || m.isUpdateReqPending,
  ).length;

  account.pendingRequestsCount = (account.projects ?? []).reduce(
    (sum, p) => sum + (p.pendingRequestsCount ?? 0),
    0,
  );
};

const flattenProjectsWithPendingCounts = (
  accounts: AccountAdminDto[],
): ProjectAdminDto[] =>
  accounts.flatMap((account) =>
    (account.projects ?? []).map((project) => {
      recomputePendingCounts(account, project);
      return project;
    }),
  );

export const returns = createSliceCustom({
  name: sowAdminStoreKey,
  initialState,
  reducers: {
    resetRequestStates: (state) => {
      state.requestMoveState = DataState.Idle;
      state.requestMoveError = null;
      state.requestEditState = DataState.Idle;
      state.requestEditError = null;
      state.requestDeactivateState = DataState.Idle;
      state.requestDeactivateError = null;
    },
    setDate: (state, { payload }: PayloadAction<string | null>) => {
      state.dateLocal = payload;
    },
    setGlobalDate: (state) => {
      state.date = state.dateLocal ?? null;
    },
    cleanDate: (state) => {
      state.dateLocal = null;
      state.date = null;
    },
    setPortfolioDate: (state, { payload }: PayloadAction<string | null>) => {
      state.portfolioDateLocal = payload;
    },
    setPortfolioGlobalDate: (state) => {
      state.portfolioDate = state.portfolioDateLocal ?? null;
    },
    cleanPortfolioDate: (state) => {
      state.portfolioDateLocal = null;
      state.portfolioDate = null;
    },
    setSowAdminFilters: (
      state,
      { payload }: PayloadAction<Partial<SowAdminState['filters']>>,
    ) => {
      state.filters = { ...state.filters, ...payload };
    },
    resetSowAdminFilters: (state) => {
      state.filters = initialState.filters;
    },
  },
  extraReducers: (builder) => {
    // fetch data
    builder
      .addCase(fetchSowAdminAccounts.pending, (state, action) => {
        if (action.meta.arg.isPortfolio) {
          state.portfolioState = DataState.Pending;
          state.portfolioError = null;
        } else {
          state.state = DataState.Pending;
          state.error = initialState.error;
        }
      })
      .addCase(fetchSowAdminAccounts.fulfilled, (state, { payload }) => {
        if (payload.isPortfolio) {
          state.portfolioState = DataState.Fulfilled;
          state.portfolioAccounts = payload.accounts;
          state.portfolioProjects = flattenProjectsWithPendingCounts(
            payload.accounts,
          );
        } else {
          state.state = DataState.Fulfilled;
          state.accounts = payload.accounts;
          state.projects = flattenProjectsWithPendingCounts(payload.accounts);

          if (state.isUniqueFilterListsDefined === false) {
            state.isUniqueFilterListsDefined = true;

            state.uniqueFilterLists.pdms = (() => {
              const filterArray: { value: string; label: string }[] = [];
              const uniqueItems: string[] = [];

              state.accounts.forEach((item) => {
                if (
                  item.pdmId &&
                  item.pdmFullName &&
                  !uniqueItems.includes(item.pdmId)
                ) {
                  uniqueItems.push(item.pdmId);
                  filterArray.push({
                    value: item.pdmId,
                    label: item.pdmFullName,
                  });
                }
              });

              return filterArray.sort((a, b) =>
                (a.label ?? '').localeCompare(b.label ?? ''),
              );
            })();

            state.uniqueFilterLists.dms = (() => {
              const filterArray: { value: string; label: string }[] = [];
              const uniqueItems: string[] = [];

              state.accounts.forEach((item) => {
                if (
                  item.dmId &&
                  item.dmFullName &&
                  !uniqueItems.includes(item.dmId)
                ) {
                  uniqueItems.push(item.dmId);
                  filterArray.push({
                    value: item.dmId,
                    label: item.dmFullName,
                  });
                }
              });

              return filterArray.sort((a, b) =>
                (a.label ?? '').localeCompare(b.label ?? ''),
              );
            })();
          }
        }
      })
      .addCase(fetchSowAdminAccounts.rejected, (state, action) => {
        if (action.meta.arg.isPortfolio) {
          state.portfolioState = DataState.Rejected;
          state.portfolioError = {
            message: `Oops. Something went wrong. :(`,
          };
        } else {
          state.state = DataState.Rejected;
          state.error = {
            message: `Oops. Something went wrong. :(`,
          };
        }
      })

      .addCase(requestMove.pending, (state) => {
        state.requestMoveState = DataState.Pending;
        state.requestMoveError = initialState.requestMoveError;
      })
      .addCase(
        requestMove.fulfilled,
        (
          state,
          {
            payload,
          }: PayloadAction<SowChangeReqDto | ExecuteSowChangeReqRespDto>,
        ) => {
          state.requestMoveState = DataState.Fulfilled;

          if ('idType' in payload) {
            // Handle ExecuteSowChangeReqRespDto
            const { createdResources, updatedResources } = payload;

            if (state.accounts) {
              state.accounts = updateWithExecutedSowChangeReq(
                state.accounts,
                createdResources,
                updatedResources,
              );

              state.projects = state.accounts.flatMap(
                (account) => account.projects,
              );
            }
          }
        },
      )
      .addCase(requestEdit.pending, (state) => {
        state.requestEditState = DataState.Pending;
        state.requestEditError = initialState.requestEditError;
      })
      .addCase(
        requestEdit.fulfilled,
        (
          state,
          {
            payload,
          }: PayloadAction<SowChangeReqDto | ExecuteSowChangeReqRespDto>,
        ) => {
          state.requestEditState = DataState.Fulfilled;

          if ('idType' in payload) {
            // Handle ExecuteSowChangeReqRespDto
            const { createdResources, updatedResources } = payload;

            if (state.accounts) {
              state.accounts = updateWithExecutedSowChangeReq(
                state.accounts,
                createdResources,
                updatedResources,
              );

              state.projects = state.accounts.flatMap(
                (account) => account.projects,
              );
            }
          }

          if (payload.resourceChReqs && payload.resourceChReqs.length > 0) {
            const resourceId = payload.resourceChReqs[0].resourceId;
            for (const accountList of [
              state.accounts,
              state.portfolioAccounts,
            ]) {
              if (!accountList) continue;
              for (const account of accountList) {
                for (const project of account.projects ?? []) {
                  const resource = project.members?.find(
                    (r) => r.resourceId === resourceId,
                  );
                  if (resource) {
                    resource.isUpdateReqPending = true;
                    recomputePendingCounts(account, project);
                  }
                }
              }
            }
          }
        },
      )
      .addCase(requestEdit.rejected, (state) => {
        state.requestEditState = DataState.Rejected;
        state.requestEditError = {
          message: `Oops. Something went wrong. :(`,
        };
      })
      .addCase(requestDeactivate.pending, (state) => {
        state.requestDeactivateState = DataState.Pending;
        state.requestDeactivateError = initialState.requestDeactivateError;
      })
      .addCase(
        requestDeactivate.fulfilled,
        (
          state,
          {
            payload,
          }: PayloadAction<SowChangeReqDto | ExecuteSowChangeReqRespDto>,
        ) => {
          state.requestDeactivateState = DataState.Fulfilled;

          if ('idType' in payload) {
            // Handle ExecuteSowChangeReqRespDto
            const { createdResources, updatedResources } = payload;

            if (state.accounts) {
              state.accounts = updateWithExecutedSowChangeReq(
                state.accounts,
                createdResources,
                updatedResources,
              );

              state.projects = state.accounts.flatMap(
                (account) => account.projects,
              );
            }
            if (state.portfolioAccounts) {
              state.portfolioAccounts = updateWithExecutedSowChangeReq(
                state.portfolioAccounts,
                createdResources,
                updatedResources,
              );

              state.portfolioProjects = state.portfolioAccounts.flatMap(
                (account) => account.projects,
              );
            }
          }

          if (payload.resourceChReqs && payload.resourceChReqs.length > 0) {
            const resourceId = payload.resourceChReqs[0].resourceId;
            for (const accountList of [
              state.accounts,
              state.portfolioAccounts,
            ]) {
              if (!accountList) continue;
              for (const account of accountList) {
                for (const project of account.projects ?? []) {
                  const resource = project.members?.find(
                    (r) => r.resourceId === resourceId,
                  );
                  if (resource) {
                    resource.isDeleteReqPending = true;
                    recomputePendingCounts(account, project);
                  }
                }
              }
            }
          }
        },
      )
      .addCase(requestDeactivate.rejected, (state) => {
        state.requestDeactivateState = DataState.Rejected;
        state.requestDeactivateError = {
          message: `Oops. Something went wrong. :(`,
        };
      })
      .addCase(requestBulkDeactivate.pending, (state) => {
        state.requestDeactivateState = DataState.Pending;
        state.requestDeactivateError = initialState.requestDeactivateError;
      })
      .addCase(
        requestBulkDeactivate.fulfilled,
        (
          state,
          {
            payload,
          }: PayloadAction<SowChangeReqDto | ExecuteSowChangeReqRespDto>,
        ) => {
          state.requestDeactivateState = DataState.Fulfilled;

          if ('idType' in payload) {
            // Handle ExecuteSowChangeReqRespDto
            const { createdResources, updatedResources } = payload;

            if (state.accounts) {
              state.accounts = updateWithExecutedSowChangeReq(
                state.accounts,
                createdResources,
                updatedResources,
              );

              state.projects = state.accounts.flatMap(
                (account) => account.projects,
              );
            }
            if (state.portfolioAccounts) {
              state.portfolioAccounts = updateWithExecutedSowChangeReq(
                state.portfolioAccounts,
                createdResources,
                updatedResources,
              );

              state.portfolioProjects = state.portfolioAccounts.flatMap(
                (account) => account.projects,
              );
            }
          }

          if (payload.resourceChReqs && payload.resourceChReqs.length > 0) {
            const pendingResourceIds = payload.resourceChReqs.map(
              (r) => r.resourceId,
            );
            for (const accountList of [
              state.accounts,
              state.portfolioAccounts,
            ]) {
              if (!accountList) continue;
              for (const account of accountList) {
                for (const project of account.projects ?? []) {
                  for (const resource of project.members ?? []) {
                    if (pendingResourceIds.includes(resource.resourceId)) {
                      resource.isDeleteReqPending = true;
                      recomputePendingCounts(account, project);
                    }
                  }
                }
              }
            }
          }
        },
      )
      .addCase(requestBulkDeactivate.rejected, (state) => {
        state.requestDeactivateState = DataState.Rejected;
        state.requestDeactivateError = {
          message: 'Error deactivating multiple resources',
        };
      })
      .addCase(executeSowChangeReq.pending, (state, action) => {
        state.executeSowChangeReqState = DataState.Pending;
        state.executeSowChangeReqError = initialState.executeSowChangeReqError;
        state.sowChangeReqId = action.meta.arg.id;
      })
      .addCase(
        executeSowChangeReq.fulfilled,
        (state, { payload }: PayloadAction<ExecuteSowChangeReqRespDto>) => {
          state.executeSowChangeReqState = DataState.Fulfilled;
          if (state.accounts) {
            state.accounts = updateWithExecutedSowChangeReq(
              state.accounts,
              payload.createdResources,
              payload.updatedResources,
            );

            state.projects = state.accounts.flatMap(
              (account) => account.projects,
            );
          }
          if (state.portfolioAccounts) {
            state.portfolioAccounts = updateWithExecutedSowChangeReq(
              state.portfolioAccounts,
              payload.createdResources,
              payload.updatedResources,
            );

            state.portfolioProjects = state.portfolioAccounts.flatMap(
              (account) => account.projects,
            );
          }

          if (state.sowChangeReqId) {
            state.sowChangeReqStatuses[state.sowChangeReqId] = {
              status: true,
              emailSentStatus: extOperStatusToBoolean(payload.emailSentStatus),
              sowAppendStatus: extOperStatusToBoolean(payload.sowAppendStatus),
            };
          }
        },
      )
      .addCase(executeSowChangeReq.rejected, (state) => {
        state.executeSowChangeReqState = DataState.Rejected;
        state.executeSowChangeReqError = {
          message: `Oops. Something went wrong. :(`,
        };
        state.sowChangeReqId = null;
      })
      .addCase(rejectSowChangeReq.pending, (state, action) => {
        state.rejectSowChangeReqState = DataState.Pending;
        state.rejectSowChangeReqError = initialState.rejectSowChangeReqError;
        state.sowChangeReqId = action.meta.arg.id;
      })
      .addCase(rejectSowChangeReq.fulfilled, (state) => {
        state.rejectSowChangeReqState = DataState.Fulfilled;
        if (state.sowChangeReqs) {
          state.sowChangeReqs.sowChangeReqs =
            state.sowChangeReqs.sowChangeReqs.filter(
              (sowChangeReq) => sowChangeReq.id !== state.sowChangeReqId,
            );
        }
      })
      .addCase(rejectSowChangeReq.rejected, (state) => {
        state.rejectSowChangeReqState = DataState.Rejected;
        state.rejectSowChangeReqError = {
          message: `Oops. Something went wrong. :(`,
        };
      })
      .addCase(fetchSowChangeReqs.pending, (state) => {
        state.state = DataState.Pending;
        state.error = initialState.error;
      })
      .addCase(
        fetchSowChangeReqs.fulfilled,
        (state, { payload }: PayloadAction<SowChangeReqForApprovalRespDto>) => {
          state.state = DataState.Fulfilled;
          state.sowChangeReqs = payload;
          state.sowChangeReqStatuses = {};
        },
      )
      .addCase(fetchSowChangeReqs.rejected, (state) => {
        state.state = DataState.Rejected;
        state.error = {
          message: `Oops. Something went wrong. :(`,
        };
      });
  },
});

const updateWithExecutedSowChangeReq = (
  accounts: AccountAdminDto[],
  createdResources: ResourceDto[],
  updatedResources: ResourceDto[],
): AccountAdminDto[] => {
  const result: AccountAdminDto[] = [];
  const allMembers: {
    fullName: string;
    avatarUrl: string | null;
    userId: string;
  }[] = accounts.flatMap((account) =>
    account.projects.flatMap((project) =>
      project.members.map((member) => ({
        fullName: member.fullName,
        avatarUrl: member.avatarUrl,
        userId: member.id,
      })),
    ),
  );
  // First loop - handle updated resources
  for (const account of accounts) {
    const updatedProjects = [];

    for (const project of account.projects) {
      const updatedMembers: UserForAdminDto[] = [];

      for (const updatedResource of updatedResources) {
        const member = project.members.find(
          (m) => m.resourceId === updatedResource.id,
        );

        if (!member) continue;

        // Skip if resource was made inactive
        if (updatedResource.status === ResourceStatus.INACTIVE) {
          continue;
        }

        updatedMembers.push({
          ...member,
          seniority: updatedResource.seniority,
          billRate: updatedResource.billRate,
          allocation: updatedResource.allocation,
          designation: updatedResource.position,
          status: updatedResource.status,
          startedAt: updatedResource.startedAt,
          endedAt: updatedResource.endedAt,
        });
      }

      // Keep existing members that weren't updated
      const existingMembers = project.members.filter(
        (member) => !updatedResources.find((r) => r.id === member.resourceId),
      );

      updatedProjects.push({
        ...project,
        members: [...updatedMembers, ...existingMembers],
      });
    }

    result.push({
      ...account,
      projects: updatedProjects,
    });
  }

  // Second loop - handle created resources
  for (const account of result) {
    for (const project of account.projects) {
      for (const createdResource of createdResources) {
        if (createdResource.projectId !== project.id) continue;

        const sourceProject = account.projects.find(
          (p) => p.id === createdResource.projectId,
        );
        if (!sourceProject) continue;

        const sourceMember = allMembers.find(
          (m) => m.userId === createdResource.userId,
        );
        if (!sourceMember) continue;

        // Add new member to project
        project.members.push({
          ...createdResource,
          fullName: sourceMember.fullName,
          designation: createdResource.position,
          seniority: createdResource.seniority,
          billRate: createdResource.billRate,
          allocation: createdResource.allocation,
          status: createdResource.status,
          avatarUrl: sourceMember.avatarUrl,
          resourceId: createdResource.id,
          startedAt: createdResource.startedAt,
          endedAt: createdResource.endedAt,
        });
      }
    }
  }

  return result;
};

export const sowAdminSlice = returns.slice;
export const {
  resetRequestStates,
  setDate,
  setGlobalDate,
  cleanDate,
  setPortfolioDate,
  setPortfolioGlobalDate,
  cleanPortfolioDate,
  resetSowAdminFilters,
  setSowAdminFilters,
} = sowAdminSlice.actions;
export const sowAdminReducers = returns.reducers;
export const sowAdminExtraReducers = returns.extraReducers;
